import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Paper,
  Alert,
  Stack,
  Switch,
  FormControlLabel,
  Divider,
  Button,
} from '@mui/material';
import { CalculatorConfig, CalculatorExecutionResult } from '../types';
import { runPipeline } from '../engine/pipeline';
import {
  getDefaultValueForInput,
  getDefaultCalculatorValues,
  useCalculatorRunnerState,
} from '../useCalculatorRunnerState';

export interface CalculatorRunnerProps {
  config: CalculatorConfig;
  inputValues?: Record<string, any>;
  updateInputValues?: (
    updater: Record<string, any> | ((prev: Record<string, any>) => Record<string, any>) | string,
    fieldValue?: any
  ) => void;
  disabled?: boolean;
  initialValues?: Record<string, any>;
  onChange?: (result: CalculatorExecutionResult) => void;
  noTitle?: boolean;
}

export const CalculatorRunner: React.FC<CalculatorRunnerProps> = ({
  config,
  inputValues: controlledInputValues,
  updateInputValues: controlledUpdateInputValues,
  disabled = false,
  initialValues,
  onChange,
  noTitle = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showIntermediary, setShowIntermediary] = useState(false);
  const [revealedOptionalFields, setRevealedOptionalFields] = useState<Record<string, boolean>>({});
  const [autoFocusField, setAutoFocusField] = useState<string | null>(null);

  // If controlled values are not provided, maintain internal state via useCalculatorRunnerState
  const internalState = useCalculatorRunnerState(config, initialValues);

  const inputValues = controlledInputValues ?? internalState.inputValues;
  const updateInputValues = controlledUpdateInputValues ?? internalState.updateInputValues;

  // When controlled, ensure any missing input defaults are populated back into updateInputValues
  useEffect(() => {
    if (controlledInputValues && controlledUpdateInputValues && config.inputs?.length) {
      const missing: Record<string, any> = {};
      let hasMissing = false;
      for (const input of config.inputs) {
        if (controlledInputValues[input.name] === undefined) {
          missing[input.name] = getDefaultValueForInput(input);
          hasMissing = true;
        }
      }
      if (hasMissing) {
        controlledUpdateInputValues((prev) => ({
          ...getDefaultCalculatorValues(config, prev),
        }));
      }
    }
  }, [config, controlledInputValues, controlledUpdateInputValues]);

  const handleChange = (name: string, value: any) => {
    if (disabled) return;
    if (updateInputValues) {
      updateInputValues(name, value);
    }
  };

  const executionResult = useMemo(() => {
    const result = runPipeline(config, inputValues);
    if (onChange) {
      onChange(result);
    }
    return result;
  }, [config, inputValues, onChange]);

  // Collect all target fields from rules
  const targetFields = Array.from(
    new Set((config.rules || []).map((r) => r.targetField).filter(Boolean))
  );

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {((!noTitle && config.title) || config.description || config.description_more) && (
        <Box>
          {!noTitle && config.title && <Typography variant="h6">{config.title}</Typography>}
          {config.description && (
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
              {config.description}
            </Typography>
          )}
          {Boolean(config.description_more?.trim()) && (
            <Box sx={{ mt: 0.5 }}>
              {!isExpanded ? (
                <Button
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={() => setIsExpanded(true)}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none', verticalAlign: 'baseline' }}
                >
                  <FormattedMessage id="common.expand" />
                </Button>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                    {config.description_more}
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    onClick={() => setIsExpanded(false)}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', mt: 0.5 }}
                  >
                    <FormattedMessage id="common.collapse" />
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* Inputs block */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(config.inputs || [])
          .filter(
            (input) => !input.optional || revealedOptionalFields[input.name]
          )
          .map((input) => {
            if (input.type === 'separator') {
              if (input.label && input.label.trim()) {
                return (
                  <Box key={input.id || input.name} sx={{ my: 1 }}>
                    <Divider textAlign="left">
                      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {input.label}
                      </Typography>
                    </Divider>
                  </Box>
                );
              }
              return <Divider key={input.id || input.name} sx={{ my: 1 }} />;
            }

            const val = inputValues[input.name] ?? getDefaultValueForInput(input);
            const hasError = Boolean(
              executionResult.missingFields?.includes(input.name) || executionResult.fieldErrors?.[input.name]
            );
            const errorText = executionResult.fieldErrors?.[input.name];
            const helperText =
              errorText || input.description || (input.unit ? `Единица измерения: ${input.unit}` : undefined);
            const isAutoFocus = autoFocusField === input.name;

            if (input.type === 'number') {
              return (
                <TextField
                  key={input.id}
                  id={input.id}
                  name={input.name}
                  label={input.label}
                  type="number"
                  size="small"
                  fullWidth
                  autoFocus={isAutoFocus}
                  disabled={disabled}
                  error={hasError}
                  value={val}
                  onChange={(e) => {
                    const numVal = e.target.value === '' ? '' : Number(e.target.value);
                    handleChange(input.name, numVal);
                  }}
                  inputProps={{
                    min: input.min,
                    max: input.max,
                    step: input.step,
                  }}
                  helperText={helperText}
                />
              );
            }

            if (input.type === 'select') {
              return (
                <FormControl key={input.id} size="small" fullWidth disabled={disabled} error={hasError}>
                  <InputLabel id={`label-${input.id}`}>{input.label}</InputLabel>
                  <Select
                    labelId={`label-${input.id}`}
                    id={input.id}
                    name={input.name}
                    value={val}
                    label={input.label}
                    autoFocus={isAutoFocus}
                    onChange={(e) => {
                      const selectedOpt = input.options?.find(
                        (opt) => String(opt.value) === String(e.target.value)
                      );
                      const optVal = selectedOpt ? selectedOpt.value : e.target.value;
                      handleChange(input.name, optVal);
                    }}
                  >
                    {input.options?.map((opt) => (
                      <MenuItem key={String(opt.value)} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {helperText && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
              );
            }

            if (input.type === 'boolean') {
              return (
                <FormControlLabel
                  key={input.id}
                  control={
                    <Switch
                      checked={Boolean(val)}
                      disabled={disabled}
                      autoFocus={isAutoFocus}
                      onChange={(e) => handleChange(input.name, e.target.checked)}
                    />
                  }
                  label={input.label}
                />
              );
            }

            return (
              <TextField
                key={input.id}
                id={input.id}
                name={input.name}
                label={input.label}
                type="text"
                size="small"
                fullWidth
                autoFocus={isAutoFocus}
                disabled={disabled}
                error={hasError}
                value={val}
                onChange={(e) => handleChange(input.name, e.target.value)}
                helperText={helperText}
              />
            );
          })}

        {/* Optional inputs toggles */}
        {(() => {
          const hiddenOptionalInputs = (config.inputs || []).filter(
            (input) => input.optional && !revealedOptionalFields[input.name]
          );

          if (hiddenOptionalInputs.length === 0) return null;

          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
              {hiddenOptionalInputs.map((input) => (
                <Button
                  key={input.id}
                  size="small"
                  variant="text"
                  color="primary"
                  onClick={() => {
                    setRevealedOptionalFields((prev) => ({ ...prev, [input.name]: true }));
                    setAutoFocusField(input.name);
                  }}
                  sx={{ textTransform: 'none', px: 0, minWidth: 'auto', fontSize: '0.8125rem' }}
                >
                  + {input.label || input.name}
                </Button>
              ))}
            </Box>
          );
        })()}
      </Box>

      <Divider />

      {/* Outputs block */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Результаты расчета
        </Typography>

        {executionResult.errors.length > 0 && (
          <Stack spacing={1}>
            {executionResult.errors.map((err, idx) => (
              <Alert key={idx} severity="error">
                {err}
              </Alert>
            ))}
          </Stack>
        )}

        {(() => {
          // If config.outputs are configured, sort target fields according to outputs order, followed by unconfigured target fields
          const outputsOrder = (config.outputs || []).map((o) => o.field);
          const sortedTargetFields = [...targetFields].sort((a, b) => {
            const indexA = outputsOrder.indexOf(a);
            const indexB = outputsOrder.indexOf(b);
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return 0;
          });

          let hasIntermediaryFields = false;

          const renderedFields = sortedTargetFields.map((field) => {
            const outputConfig = (config.outputs || []).find((o) => o.field === field);
            const ruleForField = (config.rules || []).find((r) => r.targetField === field);

            if (outputConfig?.is_hidden) {
              return null;
            }

            const isConfigured = Boolean(config.outputs && config.outputs.length > 0);
            const isResult = outputConfig ? outputConfig.isResult !== false : true;
            const isIntermediary = outputConfig ? Boolean(outputConfig.isIntermediary) : false;

            if (isConfigured && !isResult && !isIntermediary) {
              return null;
            }

            if (isIntermediary) {
              hasIntermediaryFields = true;
              if (!showIntermediary) {
                return null;
              }
            }

            const displayName = outputConfig?.name || ruleForField?.name || field;
            const resultValue = executionResult.values[field];
            const traceStep = ruleForField ? executionResult.trace.find((t) => t.ruleId === ruleForField.id) : undefined;

            return (
              <Paper
                key={field}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: isIntermediary ? 'action.hover' : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {displayName}
                  </Typography>
                </Box>
                <Typography variant={isIntermediary ? 'body1' : 'h5'} sx={{ fontWeight: 600 }}>
                  {resultValue === undefined || resultValue === null || (typeof resultValue === 'number' && Number.isNaN(resultValue))
                    ? '…'
                    : typeof resultValue === 'number'
                    ? resultValue.toLocaleString('ru-RU')
                    : String(resultValue)}
                </Typography>
                {traceStep && !traceStep.conditionMatched && (
                  <Typography variant="caption" color="text.secondary">
                    (условие не выполнено)
                  </Typography>
                )}
              </Paper>
            );
          });

          return (
            <>
              {renderedFields}
              {hasIntermediaryFields && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1 }}>
                  <Button
                    size="small"
                    variant="text"
                    color="primary"
                    onClick={() => setShowIntermediary((prev) => !prev)}
                    sx={{ p: 0, minWidth: 0, textTransform: 'none', fontSize: '0.8125rem' }}
                  >
                    {showIntermediary ? 'скрыть промежуточные' : 'показать промежуточные'}
                  </Button>
                </Box>
              )}
            </>
          );
        })()}
      </Box>
    </Box>
  );
};

export default CalculatorRunner;
